import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { RefreshCwIcon } from 'lucide-react';

import { Button } from 'components/Button';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Textarea } from 'components/Textarea';
import { Divider, Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { Commodity, NormalizedLoad, TMSEquipmentType } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

import { LoadBuildingTextInput } from './LoadBuildingTextInput';
import { accessorialsFromRateLineItems } from './accessorialOptions';
import { CommodityItemInputCard } from './components/CommodityItemInputCard';
import {
  ensureValidMGBCommodityLegIndices,
  firstMGBPickupStopIndex,
  hydrateMGBCommodityStopFieldsFromForm,
  lastMGBDropStopIndex,
} from './mgbStops';
import {
  type MGBSuggestionMaps,
  createMGBCommodity,
  hasMGEquipmentParenCode,
  resolveMGBEquipment,
  sumMGBCommodityWeightLbs,
} from './utils';

function equipmentTypePlaceholder(
  customerSelected: boolean,
  isLoading: boolean,
  equipmentCount: number
): string {
  if (!customerSelected) {
    return 'Select a customer first';
  }
  if (isLoading) {
    return 'Loading equipment types…';
  }
  if (equipmentCount === 0) {
    return 'No equipment types found';
  }
  return EQUIPMENT_PLACEHOLDER;
}

type SpecificationsFormProps = {
  equipmentTypes: MaybeUndef<TMSEquipmentType[]>;
  isLoading: boolean;
  suggestionMaps: MGBSuggestionMaps;
  customerSelected?: boolean;
  equipmentSelectKey?: string;
  onRefreshEquipmentTypes?: () => void;
  onJumpToRatesAccessorials?: () => void;
  onUnresolvedEquipmentCleared?: () => void;
};

export default function SpecificationsForm({
  equipmentTypes,
  isLoading,
  suggestionMaps,
  customerSelected = true,
  equipmentSelectKey = 'default',
  onRefreshEquipmentTypes,
  onJumpToRatesAccessorials,
  onUnresolvedEquipmentCleared,
}: SpecificationsFormProps): React.JSX.Element {
  const formMethods = useFormContext<NormalizedLoad>();
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = formMethods;

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'commodities',
  });

  /**
   * Append a blank commodity that is not dirty (no yellow fields).
   * Extends the original defaults for the new row only — do not reset from
   * getValues(), or prior edits lose dirty highlighting.
   * Multi-stop: default Picked At / Delivered To to first pickup → last drop.
   */
  const appendCommodityWithoutDirty = useCallback(() => {
    const stops = getValues('stops') ?? [];
    const defaults =
      stops.length > 2
        ? {
            pickupStopNumber: firstMGBPickupStopIndex(stops),
            dropStopNumber: lastMGBDropStopIndex(stops),
          }
        : undefined;
    const row = createMGBCommodity(defaults);
    const defaultCommodities = Array.isArray(control._defaultValues.commodities)
      ? control._defaultValues.commodities
      : [];
    // Baseline the new row before append so dirty recompute keeps prior edits
    // and leaves the blank row pristine.
    control._defaultValues.commodities = [...defaultCommodities, row];
    append(row);
  }, [append, control, getValues]);

  const selectableEquipment = useMemo(
    () => (equipmentTypes ?? []).filter((eq) => Boolean(eq.id)),
    [equipmentTypes]
  );
  const equipmentById = useMemo(() => {
    const map = new Map<string, TMSEquipmentType>();
    for (const eq of selectableEquipment) {
      map.set(eq.id, eq);
    }
    return map;
  }, [selectableEquipment]);

  const watchedLineItems = watch('rateData.customerLineItems');
  const accessorialCount =
    accessorialsFromRateLineItems(watchedLineItems).length;

  const watchedTransportType = useWatch({
    control,
    name: 'specifications.transportType',
  });

  // Resolve AI / leftover free-text transport types onto customer-scoped
  // equipment ids using the tenant maps passed in. Re-run when the field
  // changes (suggestion switch / reset) even if equipment options are unchanged.
  // Stored MG labels like "Cargo Van - Sprinter (CV)" must map to id "CV" for
  // the Select; never clear those — wiping them leaves edit/search empty.
  useEffect(() => {
    if (!customerSelected || isLoading || !Array.isArray(equipmentTypes)) {
      return;
    }
    const current = watchedTransportType?.trim() ?? '';
    if (!current) {
      return;
    }

    if (equipmentById.has(current)) {
      const currentSize =
        getValues('specifications.transportSize')?.trim() ?? '';
      if (!currentSize) {
        const picked = equipmentById.get(current);
        setValue('specifications.transportSize', picked?.name ?? '', {
          shouldDirty: false,
          shouldValidate: false,
        });
      }
      return;
    }

    const resolved = resolveMGBEquipment(
      current,
      selectableEquipment,
      suggestionMaps
    );
    if (resolved) {
      setValue('specifications.transportType', resolved.id, {
        shouldDirty: false,
        shouldValidate: false,
      });
      setValue('specifications.transportSize', resolved.name, {
        shouldDirty: false,
        shouldValidate: false,
      });
      const specsDefault = control._defaultValues.specifications ?? {};
      control._defaultValues.specifications = {
        ...specsDefault,
        transportType: resolved.id,
        transportSize: resolved.name,
      };
      return;
    }

    if (hasMGEquipmentParenCode(current)) {
      return;
    }

    setValue('specifications.transportType', '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('specifications.transportSize', '', {
      shouldDirty: true,
      shouldValidate: false,
    });
    onUnresolvedEquipmentCleared?.();
  }, [
    control,
    customerSelected,
    equipmentById,
    equipmentTypes,
    getValues,
    isLoading,
    onUnresolvedEquipmentCleared,
    selectableEquipment,
    setValue,
    suggestionMaps,
    watchedTransportType,
  ]);

  const watchedCommodities = useWatch({ control, name: 'commodities' });
  const watchedStops = useWatch({ control, name: 'stops' });
  const watchedRouteStops = useMemo(() => watchedStops ?? [], [watchedStops]);

  // When route is multi-stop, keep every commodity pick/drop on a valid leg
  // (same resolver as submit validation / wire prep).
  useEffect(() => {
    const routeStops = watchedRouteStops ?? [];
    if (routeStops.length <= 2) {
      return;
    }
    const commodities = hydrateMGBCommodityStopFieldsFromForm(
      getValues,
      getValues('commodities') ?? []
    );
    let changed = false;
    const next = commodities.map((c) => {
      if (!c) {
        return c;
      }
      const leg = ensureValidMGBCommodityLegIndices(
        routeStops,
        c.pickupStopNumber,
        c.dropStopNumber
      );
      if (c.pickupStopNumber !== leg.pickup || c.dropStopNumber !== leg.drop) {
        changed = true;
        return {
          ...c,
          pickupStopNumber: leg.pickup,
          dropStopNumber: leg.drop,
        };
      }
      return c;
    });
    if (!changed) {
      return;
    }
    setValue('commodities', next, { shouldDirty: false, shouldTouch: false });
    next.forEach((c, i) => {
      if (!c) {
        return;
      }
      setValue(
        `commodities.${i}.pickupStopNumber`,
        c.pickupStopNumber as number,
        {
          shouldDirty: false,
          shouldTouch: false,
        }
      );
      setValue(`commodities.${i}.dropStopNumber`, c.dropStopNumber as number, {
        shouldDirty: false,
        shouldTouch: false,
      });
    });
  }, [watchedRouteStops, getValues, setValue]);

  useEffect(() => {
    if (fields.length > 0) {
      return;
    }
    const commodity = createMGBCommodity();
    control._defaultValues.commodities = [createMGBCommodity()];
    replace([commodity]);
  }, [control, fields.length, replace]);

  const updateLoadSummaryFromCommodities = useCallback(
    (commodities: Commodity[] | undefined) => {
      setValue(
        'specifications.totalWeight.val',
        sumMGBCommodityWeightLbs(commodities),
        {
          shouldDirty: false,
        }
      );
    },
    [setValue]
  );

  useEffect(() => {
    updateLoadSummaryFromCommodities(watchedCommodities);
  }, [watchedCommodities, updateLoadSummaryFromCommodities]);

  const removeCommodity = useCallback(
    (index: number) => {
      if (fields.length <= 1) {
        return;
      }
      const defaultCommodities = Array.isArray(
        control._defaultValues.commodities
      )
        ? control._defaultValues.commodities
        : [];
      // Keep baselines aligned with field-array indexes after remove, otherwise
      // remaining rows compare against the wrong default (dirty highlight drift).
      if (index >= 0 && index < defaultCommodities.length) {
        control._defaultValues.commodities = defaultCommodities.filter(
          (_, i) => i !== index
        );
      }
      remove(index);
    },
    [control, fields.length, remove]
  );

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full'>
      <div className='col-span-4'>
        <Label name='specifications.transportType' required>
          Equipment Type
        </Label>
        <Flex direction='row' gap='xs' align='center' className='mt-1 w-full'>
          <div className='min-w-0 flex-1'>
            <Controller
              name='specifications.transportType'
              control={control}
              rules={{
                validate: (value) => {
                  const trimmed = String(value ?? '').trim();
                  if (!trimmed) {
                    return EQUIPMENT_REQUIRED_MESSAGE;
                  }
                  // While options load, keep a provisional AI value — the clear
                  // effect re-validates once the list settles.
                  if (isLoading) {
                    return true;
                  }
                  // Non-empty values that are not in the Select options look
                  // empty in the UI; treat them as missing (common AI mismatch).
                  if (
                    !equipmentById.has(trimmed) &&
                    !resolveMGBEquipment(
                      trimmed,
                      selectableEquipment,
                      suggestionMaps
                    )
                  ) {
                    return EQUIPMENT_REQUIRED_MESSAGE;
                  }
                  return true;
                },
              }}
              render={({ field }) => {
                const selectValue = (() => {
                  const raw = field.value?.trim() ?? '';
                  if (!raw) {
                    return undefined;
                  }
                  if (equipmentById.has(raw)) {
                    return raw;
                  }
                  return (
                    resolveMGBEquipment(
                      raw,
                      selectableEquipment,
                      suggestionMaps
                    )?.id ?? undefined
                  );
                })();

                return (
                  <Select
                    key={`mgb-equipment-${equipmentSelectKey}-${selectValue || 'empty'}`}
                    value={selectValue}
                    onValueChange={(value) => {
                      field.onChange(value);
                      const picked = equipmentById.get(value);
                      setValue(
                        'specifications.transportSize',
                        picked?.name ?? '',
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        }
                      );
                    }}
                    disabled={
                      !customerSelected ||
                      isLoading ||
                      selectableEquipment.length === 0
                    }
                  >
                    <SelectTrigger
                      aria-label='Equipment type'
                      disabled={
                        !customerSelected ||
                        isLoading ||
                        selectableEquipment.length === 0
                      }
                      className='w-full text-[13px] border-neutral-input-border'
                    >
                      <SelectValue
                        placeholder={equipmentTypePlaceholder(
                          customerSelected,
                          isLoading,
                          selectableEquipment.length
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {selectableEquipment.map((eq) => (
                        <SelectItem key={eq.id} value={eq.id}>
                          {eq.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
          </div>
          {onRefreshEquipmentTypes ? (
            <button
              type='button'
              title='Refresh equipment types'
              aria-label='Refresh equipment types'
              onClick={onRefreshEquipmentTypes}
              disabled={!customerSelected || isLoading}
              className={`shrink-0 py-2 px-1 hover:text-info-600 rounded-full transition-colors ${
                !customerSelected || isLoading
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }`}
            >
              {isLoading ? (
                <ButtonLoader className='mr-0' />
              ) : (
                <RefreshCwIcon className='h-4 w-4' />
              )}
            </button>
          ) : null}
        </Flex>
        <ErrorMessage
          errors={errors}
          name='specifications.transportType'
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500 mt-1'>
              {message}
            </Typography>
          )}
        />
      </div>

      <div className='col-span-4 w-full'>
        <Label name='accessorials'>Accessorials</Label>
        <Typography variant='body-xs' className='text-neutral-500 mt-0.5'>
          Managed in the Rates section.
        </Typography>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='mt-2 h-8 text-[13px] text-brand-600 border-neutral-input-border'
          buttonNamePosthog={null}
          onClick={onJumpToRatesAccessorials}
          disabled={!onJumpToRatesAccessorials}
          aria-label={
            accessorialCount > 0
              ? `See Accessorials (${accessorialCount})`
              : 'Add Accessorials in Rates'
          }
        >
          {accessorialCount > 0
            ? `See Accessorials (${accessorialCount})`
            : 'Add Accessorials in Rates'}
        </Button>
      </div>

      <Divider className='col-span-4' />
      <Typography variant='h5' className='col-span-4'>
        Items <span className='text-error-500'>*</span>
      </Typography>

      <Flex
        direction='col'
        gap='lg'
        align='stretch'
        className='col-span-4 w-full'
      >
        {fields.map((field, index) => (
          <CommodityItemInputCard
            formMethods={formMethods}
            key={field.id}
            item={watch(`commodities.${index}`) || field}
            index={index}
            onRemove={removeCommodity}
            onSave={() =>
              updateLoadSummaryFromCommodities(watch('commodities'))
            }
            isDeletable={fields.length > 1}
          />
        ))}
      </Flex>

      <Flex align='center' justify='end' className='col-span-4 mr-px'>
        <Button
          type='button'
          variant='ghost'
          size='sm'
          onClick={appendCommodityWithoutDirty}
          className='h-8 px-3 text-xs text-brand-500 hover:-mx-px'
          buttonNamePosthog={ButtonNamePosthog.MGBAddCommodity}
        >
          + Add Commodity
        </Button>
      </Flex>

      <div className='col-span-4 mt-2'>
        <Typography
          variant='body-sm'
          weight='semibold'
          className='mb-4 text-neutral-700'
        >
          Load Summary (Auto-calculated)
        </Typography>
        <LoadBuildingTextInput
          name='specifications.totalWeight.val'
          label='Total Weight'
          inputType='number'
          step={0.01}
          inlineDimensionUnit='lbs'
          readOnly
        />
      </div>

      <div className='col-span-4'>
        <Label name='specifications.planningComment'>
          Special Instructions
        </Label>
        <Controller
          name='specifications.planningComment'
          control={control}
          render={({ field }) => (
            <Textarea
              className='mt-1'
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder='Driver-facing instructions for MercuryGate'
            />
          )}
        />
      </div>
    </Grid>
  );
}
